import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaConfirmFirmwareInstallationModal } from "@src/reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaRequestForApprovalProps {
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

export const useOtaRequestForApproval = ({
  activeInstallationData,
}: UseOtaRequestForApprovalProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [initiatorName, setInitiatorName] = useState<string | null>(null);
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const installationName = activeInstallationData?.firmware?.filename || "";

  const titleTemplate = isBackupInstallation
    ? i18n("modules.otaRequestForApproval.titleTemplateBackup")
    : i18n("modules.otaRequestForApproval.titleTemplateFirmware");

  const title = titleTemplate.replace("{itemName}", installationName);

  const itemNameMarker = "__ITEM_NAME__";
  const titleTemplateWithMarker = titleTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [titlePrefix, titleSuffix = ""] =
    titleTemplateWithMarker.split(itemNameMarker);

  const texts = {
    title,
    titleHasItemName: titleTemplate.includes("{itemName}"),
    titlePrefix,
    titleSuffix,
    itemName: installationName,
    descriptionTemplate: isBackupInstallation
      ? i18n("modules.otaRequestForApproval.descriptionTemplateBackup")
      : i18n("modules.otaRequestForApproval.descriptionTemplateFirmware"),
    reviewAndApproveButtonText: i18n(
      "modules.otaRequestForApproval.reviewAndApproveButtonText",
    ),
  };

  const handleReviewRequestAndApprove = () => {
    dispatch(updateShowOtaConfirmFirmwareInstallationModal(true));
  };

  useEffect(() => {
    async function fetchInitiatorName() {
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Fetching initiator details for ID:",
          activeInstallationData?.initiator_id,
        );
      const response = await getSingleUserUseCase(
        activeInstallationData?.initiator_id || "",
      );

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from getSingleUserUseCase (initiator):",
          response,
        );

      if (response.success) {
        setInitiatorName(response.data?.display_name || "");
      } else {
        setInitiatorName("");
      }
    }

    fetchInitiatorName();
  }, [activeInstallationData?.initiator_id]);

  const descriptionText = texts.descriptionTemplate.replace(
    "{initiator}",
    initiatorName || "",
  );

  return {
    texts,
    descriptionText,
    showContent: initiatorName !== null,
    handleReviewRequestAndApprove,
  };
};
